/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsReactProps } from 'echarts-for-react/lib/types';
import { echarts } from '../../lib/echarts-register';
import { useTheme } from '../../context/ThemeContext';
import { resolveChartCssVariables } from '../../utils/chart-css-var.utils';
import { readCssVar } from '../../utils/css-var.utils';
import { getChartThemeColors } from '../../utils/chart-theme.utils';

/** `ComponentProps<typeof ReactEChartsCore>` bazı TS/React sürümlerinde sınıf bileşeninde hatalı çıkarım verir. */
export type AppReactEChartsProps = Omit<EChartsReactProps, 'echarts'>;

// Vite/ESM interop: bazı ortamlarda `echarts-for-react/lib/core` bir module object döndürebilir.
// React, component yerine object görünce "Element type is invalid" hatası verir.
// Bu yüzden default export’u normalize ediyoruz.
const ReactEcharts = (
  (ReactEChartsCore as unknown as { default?: unknown })?.default ?? ReactEChartsCore
) as unknown as React.ComponentType<EChartsReactProps>;

/**
 * Tree-shake edilmiş ECharts çekirdeği ile grafik; `echarts-for-react` default import yerine kullanılır.
 */
export function AppReactECharts(props: AppReactEChartsProps) {
  const { theme } = useTheme();
  const { option, ...rest } = props;

  const normalizedOption = useMemo(() => {
    const colors = getChartThemeColors(theme === 'dark');
    const resolved = resolveChartCssVariables(
      option as Record<string, unknown>,
      (tokenName) => readCssVar(tokenName, ''),
    );
    const tooltip = resolved.tooltip;
    const legend = resolved.legend;

    return {
      ...resolved,
      backgroundColor: resolved.backgroundColor ?? 'transparent',
      color: resolved.color ?? [
        colors.accent,
        colors.statusActive,
        colors.severityWarning,
        colors.severityDanger,
        readCssVar('--stat-icon-violet', colors.textSecondary),
        colors.severityInfo,
        readCssVar('--status-expired', colors.textTertiary),
        colors.textTertiary,
      ],
      textStyle: {
        color: colors.text,
        ...(typeof resolved.textStyle === 'object' && resolved.textStyle ? resolved.textStyle : {}),
      },
      ...(tooltip && !Array.isArray(tooltip) && typeof tooltip === 'object'
        ? {
            tooltip: {
              backgroundColor: colors.tooltipBg,
              borderColor: colors.tooltipBorder,
              borderWidth: 1,
              ...tooltip,
              textStyle: {
                color: colors.text,
                ...(
                  'textStyle' in tooltip &&
                  typeof tooltip.textStyle === 'object' &&
                  tooltip.textStyle
                    ? tooltip.textStyle
                    : {}
                ),
              },
            },
          }
        : {}),
      ...(legend && !Array.isArray(legend) && typeof legend === 'object'
        ? {
            legend: {
              ...legend,
              textStyle: {
                color: colors.text,
                ...(
                  'textStyle' in legend &&
                  typeof legend.textStyle === 'object' &&
                  legend.textStyle
                    ? legend.textStyle
                    : {}
                ),
              },
            },
          }
        : {}),
    };
  }, [option, theme]);

  return (
    <ReactEcharts
      key={theme}
      echarts={echarts}
      option={normalizedOption}
      {...rest}
    />
  );
}
